/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { token } from '@atlaskit/tokens';

import { useEditorAnnotations } from '@confluence/inline-comments-hooks/entry-points/useEditorAnnotations.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { fg } from '@confluence/feature-gating';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus.ts';
import { CONTENT_STATUS_ARCHIVED } from '@confluence/archive-pages/entry-points/archivingConstants.ts';
import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { useContentType } from '@confluence/page-context';

import type {
	UnreadCommentCountUnreadCommentQuery as UnreadCommentCountUnreadCommentQuery$data,
	UnreadCommentCountUnreadCommentQueryVariables as UnreadCommentCountUnreadCommentQuery$variables,
	UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment,
	UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment_location_InlineComment,
} from './__types__/UnreadCommentCountUnreadCommentQuery.ts';
import { useUnreadComments } from './hooks/useUnreadComments.ts';
import { useViewedComments } from './hooks/useViewedComments.ts';
import {
	type AnnotationStatus,
	useDanglingAndResolvedHandler,
} from './hooks/useDanglingAndResolvedHandler.ts';
import { createUnreadCommentList } from './utils/OLD/unreadCommentCountUtils.tsx';
import { GraphQLContentStatus } from './__types__/UnreadCommentQuery.ts';
import { createUnreadCommentsSet, commentIsDangling } from './utils/unreadCommentsUtils.ts';

type DotIconProps = {
	isStandaloneComment?: boolean;
};

// This code mirrors the dot icon from the ObjectSidebarControl.tsx file.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DotIcon = styled.span<DotIconProps>({
	height: token('space.075'),
	width: token('space.075'),
	backgroundColor: token('color.icon.brand'),
	borderRadius: token('radius.full'),
	border: `${token('border.width.selected')} solid ${token('color.border.inverse')}`,
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	right: (props: DotIconProps) => (props.isStandaloneComment ? token('space.0') : '3px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	top: (props: DotIconProps) => (props.isStandaloneComment ? token('space.0') : '3px'),
});

export interface UnreadCommentCountProps {
	pageId?: string;
	pageMode?: 'view' | 'edit';
	commentsMarkedAsRead?: {
		inline: Record<string, Set<string>>; // Inline Comments
		general: Record<string, Set<string>>; // Footer Comments
	};
	isStandaloneComment?: boolean;
	addNewCommentThreads?: (commentsDataMap: any) => void;
	commentsDataMap?: { inline: Record<string, any>; general: Record<string, any> };
	orderedActiveAnnotationIdList?: AnnotationStatus[];
}

const MAX_UNREAD_COMMENTS_SUPPORTED = 1000;

const unreadCommentsQuery = gql`
	query UnreadCommentCountUnreadCommentQuery($pageId: ID!, $contentStatus: [GraphQLContentStatus]) {
		comments(pageId: $pageId, contentStatus: $contentStatus, type: UNRESOLVED, depth: ROOT) {
			nodes {
				id
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							resolved
							isDangling
						}
					}
				}
				author {
					permissionType
					... on KnownUser {
						accountId
					}
				}
				createdAt {
					value
				}
				replies {
					id
					author {
						permissionType
						... on KnownUser {
							accountId
						}
					}
					createdAt {
						value
					}
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineResolveProperties {
								resolved
							}
						}
					}
				}
			}
			totalCount
		}
	}
`;

const UnreadCommentCountInner = ({
	pageId,
	pageMode = 'view',
	commentsMarkedAsRead = {
		inline: {},
		general: {},
	},
	isStandaloneComment = false,
	addNewCommentThreads,
	commentsDataMap,
	orderedActiveAnnotationIdList,
}: UnreadCommentCountProps) => {
	const [
		{
			unreadCommentsListState,
			threadKeyToCommentIdMap,
			shouldUpdateUnreadComments,
			shouldUpdateUnreadCommentsOnFirstRender,
			allComments,
			unreadCommentIDSet,
		},
		{
			updateUnreadCommentsListState,
			updateUnreadComments,
			updateUnreadCommentsOnFirstRender,
			updateReadCommentsListState,
			updateThreadKeyToCommentIdMap,
			updateAllComments,
			setIsUnreadQueryLoading,
			updateUnreadCommentIDSet,
		},
	] = useUnreadComments();

	const { userId } = useSessionData();

	const [editorAnnotationsState] = useEditorAnnotations();
	const { annotations, pageId: editorPageId } = editorAnnotationsState;

	const isRenderer = pageMode === 'view';
	const rendererActionsResult = useRendererActions();
	const { rendererActions, pageId: rendererPageId } = rendererActionsResult || {};

	const { contentStatus } = useContentStatus();

	const [pageContextContentType] = useContentType();
	const { getViewedCommentsFresh } = useViewedComments({ pageId: pageId || '' });

	const isDanglingCallback = useCallback(
		(markerRef: string | null | undefined) => {
			return commentIsDangling({
				pageMode: isRenderer ? 'view' : 'edit',
				annotationsInEditorDoc: new Set(annotations),
				rendererAnnotations: new Set(
					rendererActions
						?.getAnnotationMarks()
						?.map((ann) => ann.attrs.id)
						.filter(Boolean) || [],
				),
			})(markerRef);
		},
		[annotations, rendererActions, isRenderer],
	);

	const { data, loading, error, variables } = useQuery<
		UnreadCommentCountUnreadCommentQuery$data,
		UnreadCommentCountUnreadCommentQuery$variables
	>(unreadCommentsQuery, {
		variables: {
			pageId: pageId || '',
			contentStatus: !isRenderer
				? [GraphQLContentStatus.CURRENT, GraphQLContentStatus.DRAFT]
				: [GraphQLContentStatus.CURRENT],
		},
		skip: !pageId,
	});

	useEffect(() => {
		// isUnreadQueryLoading is used in other areas of the code to make sure unread logic is processed before further logic
		setIsUnreadQueryLoading(loading);
	}, [loading, setIsUnreadQueryLoading]);

	useEffect(() => {
		if (pageId) {
			if (!fg('confluence_frontend_unread_comments_fixes')) {
				updateUnreadCommentsListState(() => []);
				updateAllComments([]);
			} else {
				updateUnreadCommentIDSet(new Set(), 'removeAll');
			}

			// reset so we can recreate the set for the page that the user is changing to
			updateUnreadCommentsOnFirstRender(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageId]);

	const resetStates = useCallback(() => {
		updateUnreadComments(false);
		updateUnreadCommentsOnFirstRender(false);
	}, [updateUnreadComments, updateUnreadCommentsOnFirstRender]);

	// TO REMOVE OLD EXPERIENCE
	useEffect(() => {
		if (!fg('confluence_frontend_unread_comments_fixes')) {
			const commentNodes =
				(data?.comments?.nodes?.filter(
					(node) =>
						node !== null &&
						!(
							node.location as UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment_location_InlineComment
						).inlineResolveProperties?.isDangling,
				) as UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment[]) ||
				[];

			if (commentNodes.length === 0) {
				return;
			}
			updateAllComments(commentNodes);
		}
	}, [data, loading, updateAllComments]);

	const handleUpdateUnreadComments = useCallback(async () => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			if (userId && shouldUpdateUnreadCommentsOnFirstRender) {
				if (loading) {
					return [];
				}

				if (!pageId) {
					return;
				}

				const currentAllCommentsFromQuery = (() => {
					// Validate that the query was executed for the current page
					if (variables?.pageId !== pageId) {
						return [];
					}

					const filteredNodes =
						(data?.comments?.nodes?.filter(
							(node) => node !== null,
						) as UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment[]) ||
						[];

					if (filteredNodes.length === 0) {
						return [];
					}

					return filteredNodes;
				})();

				if (currentAllCommentsFromQuery.length === 0) {
					resetStates(); // we no longer need to create the set if there are no comments on the page
					return;
				}

				// Skip if renderer actions or editor annotations are for different page
				if (
					(isRenderer && rendererActions && rendererPageId !== pageId) ||
					(!isRenderer && annotations && editorPageId !== pageId)
				) {
					return; // Wait for next rerender when rendererActions / annotations are updated for the current page
				}

				const rendererAnnotations = rendererActions?.getAnnotationMarks() || [];
				const annotationsInEditorDoc = new Set<string>(annotations || []);

				if (currentAllCommentsFromQuery.length < MAX_UNREAD_COMMENTS_SUPPORTED) {
					const shouldCheckDanglingComments =
						pageContextContentType == ContentType.PAGE ||
						pageContextContentType == ContentType.BLOGPOST;
					const isArchived = contentStatus === CONTENT_STATUS_ARCHIVED;
					// skip creating unread comments set if the page is archived
					// and if annotation sets aren't ready yet
					if (
						isArchived ||
						(shouldCheckDanglingComments && isRenderer && rendererAnnotations?.length === 0) ||
						(!isRenderer && annotationsInEditorDoc.size === 0)
					) {
						return;
					}

					const viewedComments = await getViewedCommentsFresh();
					if (viewedComments.success && viewedComments.data) {
						void createUnreadCommentsSet({
							allComments: currentAllCommentsFromQuery,
							currentUserId: userId,
							pageId,
							updateUnreadCommentIDSet,
							resetStates,
							viewedComments: viewedComments.data,
							isDanglingCallback,
							addNewCommentThreads,
						});
					}
				}
			}
		} else {
			// TO REMOVE OLD EXPERIENCE
			if (allComments.length === 0) {
				return;
			}

			if (
				userId &&
				((isRenderer && shouldUpdateUnreadComments) || shouldUpdateUnreadCommentsOnFirstRender)
			) {
				const annotationsInEditorDoc = new Set<string>(annotations);

				if (allComments.length < MAX_UNREAD_COMMENTS_SUPPORTED) {
					void createUnreadCommentList({
						allComments,
						updateUnreadCommentsListState,
						userId,
						pageId,
						updateReadCommentsListState,
						updateThreadKeyToCommentIdMap,
						threadKeyToCommentIdMap,
						resetStates,
						pageMode,
						annotationsInEditorDoc,
					});
				}
			}
		}
	}, [
		allComments, // from old unread RSS state
		userId,
		isRenderer,
		shouldUpdateUnreadComments,
		shouldUpdateUnreadCommentsOnFirstRender,
		annotations,
		pageContextContentType,
		rendererActions,
		contentStatus,
		pageId,
		pageMode,
		updateUnreadCommentIDSet,
		resetStates,
		updateUnreadCommentsListState,
		updateReadCommentsListState,
		updateThreadKeyToCommentIdMap,
		threadKeyToCommentIdMap,
		getViewedCommentsFresh,
		isDanglingCallback,
		addNewCommentThreads,
		loading,
		variables,
		data,
		rendererPageId,
		editorPageId,
	]);

	useEffect(() => {
		void handleUpdateUnreadComments();
	}, [handleUpdateUnreadComments, rendererActions, annotations]);

	// Use the dangling and resolved handler hook
	useDanglingAndResolvedHandler({
		orderedActiveAnnotationIdList: orderedActiveAnnotationIdList || [],
		commentsDataMap: commentsDataMap || { inline: {}, general: {} },
		userId: userId || '',
		pageId: pageId || '',
		isDanglingCallback,
	});

	if (loading) {
		return null;
	}

	if (error) {
		markErrorAsHandled(error);
		resetStates();
		return null;
	}

	// TO REMOVE OLD EXPERIENCE
	const relevantUnreadCommentsListState =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!fg('confluence_frontend_comments_panel_v2')
			? unreadCommentsListState?.filter((comment) => comment.commentType === 'inline')
			: unreadCommentsListState;
	const commentsMarkedToBeRead =
		(Object.keys(commentsMarkedAsRead?.inline)?.length ?? 0) +
		(Object.keys(commentsMarkedAsRead?.general)?.length ?? 0);
	const unreadCommentCount =
		commentsMarkedToBeRead && fg('confluence-frontend-comments-panel')
			? relevantUnreadCommentsListState?.length - commentsMarkedToBeRead
			: relevantUnreadCommentsListState?.length;

	if (fg('confluence_frontend_unread_comments_fixes')) {
		return unreadCommentIDSet.size > 0 && !isStandaloneComment ? (
			<>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<DotIcon
					isStandaloneComment={isStandaloneComment}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
					className="unread-dot" // As far as I can tell, this is not used anywhere. But it's only recently gotten flagged & need VR tests present before we can remove it.
					data-testid="dot-variation"
				/>
			</>
		) : null;
	} else {
		// TO REMOVE OLD EXPERIENCE
		return unreadCommentCount > 0 ? (
			<>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<DotIcon
					isStandaloneComment={isStandaloneComment}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
					className="unread-dot" // As far as I can tell, this is not used anywhere. But it's only recently gotten flagged & need VR tests present before we can remove it.
					data-testid="dot-variation"
				/>
			</>
		) : null;
	}
};

export const UnreadCommentCount = withTransparentErrorBoundary({
	attribution: Attribution.COMMENTS,
})(UnreadCommentCountInner);
